import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { onlineManagerApi } from '@/api/services/onlineManager';
import { ContextHeader, type OnlineRowContext } from './RowContext';

interface Props {
  ctx: OnlineRowContext;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangePasswordModal({ ctx, onClose, onSuccess }: Props) {
  const [passwordType, setPasswordType] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const { data: passwordTypes = [] } = useQuery({
    queryKey: ['online-manager', 'password-types'],
    queryFn: onlineManagerApi.getPasswordTypes,
    staleTime: Infinity,
  });

  const typeErr = passwordType === '' ? 'Password Type is required.' : '';
  const newErr = newPassword === '' ? 'New Password is required.' : '';
  const confirmErr = confirm === '' ? 'Confirm Password is required.' : confirm !== newPassword ? 'Passwords do not match.' : '';
  const valid = !typeErr && !newErr && !confirmErr;

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    await onlineManagerApi.changePassword({
      login: ctx.login,
      dataSourceId: ctx.dataSourceId,
      passwordType: Number(passwordType),
      newPassword,
      confirmNewPassword: confirm,
    });
    onSuccess();
  };

  return (
    <Modal
      show
      title="Change Password"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-danger btn-sm" onClick={onClose}>Cancel</button>
          <AsyncButton className="btn btn-primary btn-sm" onClick={submit}>Change Password</AsyncButton>
        </>
      }
    >
      <ContextHeader ctx={ctx} />
      <div className="mb-3">
        <label className="form-label">Password Type <span className="text-danger">*</span></label>
        <Select2 placeholder="Select password type" allowClear value={passwordType} options={passwordTypes} onChange={(v) => setPasswordType(v ?? '')} />
        {touched && typeErr && <div className="text-danger small mt-1">{typeErr}</div>}
      </div>
      <div className="mb-3">
        <label className="form-label">New Password <span className="text-danger">*</span></label>
        <input type="password" className={`form-control${touched && newErr ? ' is-invalid' : ''}`} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        {touched && newErr && <div className="invalid-feedback d-block">{newErr}</div>}
      </div>
      <div className="mb-0">
        <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
        <input type="password" className={`form-control${touched && confirmErr ? ' is-invalid' : ''}`} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        {touched && confirmErr && <div className="invalid-feedback d-block">{confirmErr}</div>}
      </div>
    </Modal>
  );
}
